import { createContext, useContext, type Context, type ReactNode } from 'react';

/**
 * Host (web) and plugin chunks can each evaluate this module. A module-local
 * `createContext` would then be two contexts, and the Connection card would
 * wrap org-setup in a full `PageLayout` (`md:p-6`) while GitHub (no layout
 * wrapper) stays flush. Share one context via `Symbol.for`.
 */
const EMBED_CONTEXT_KEY = Symbol.for('reflex.integration-embed');

function embedContext(): Context<boolean> {
  const g = globalThis as typeof globalThis & { [EMBED_CONTEXT_KEY]?: Context<boolean> };
  g[EMBED_CONTEXT_KEY] ??= createContext(false);
  return g[EMBED_CONTEXT_KEY];
}

/**
 * Marks the subtree as nested inside a host surface that already has page
 * identity (plugin detail Connection card, org-setup wizard sheet).
 * {@link IntegrationPageLayout} skips `PageLayout` padding/scroll, and
 * {@link PageHeader} drops the boxed title so the panel is a ledger, not a
 * second page.
 */
export function IntegrationEmbedProvider({ children }: { children: ReactNode }) {
  const Ctx = embedContext();
  return <Ctx.Provider value={true}>{children}</Ctx.Provider>;
}

export function useIntegrationEmbedded(): boolean {
  return useContext(embedContext());
}
